import React, { useEffect, useCallback } from "react";
import { FormHelperText, Box, Grid } from "@material-ui/core";
import { useDispatch, useSelector } from "react-redux";
import { convertToRaw } from "draft-js";
import { useFormikContext } from "formik";
import FormatIndentIncreaseIcon from "@material-ui/icons/FormatIndentIncrease";
import MUIRichTextEditor from "mui-rte";
// Custom form components
import CustomCheckbox from "../../../../forms/CustomCheckbox";
import CustomSelect from "../../../../forms/selects/CustomSelect";
import { Tags } from "./tags/Tags";
import { ELearningFormData } from "../../../../../models/elearning";
import { EventStatus } from "../../../../../models/enums/eventStatus.enum";
import { EventFormData } from "../../../../../models/eventData";
// Redux
import { AppState } from "../../../../../redux";
import { getCourseLanguages } from "../../../../../redux/courseLanguage/actions";
import { getCourseSubtitles } from "../../../../../redux/courseSubtitles/actions";
import { getCourseTopics } from "../../../../../redux/courseTopics/actions";
// Styles
import { useStyles } from "./Content.styles";
import "./Content.scss";

/**
 * Content.tsx
 *
 * This component handles the course content section of an event or e-learning form.
 * It supports selecting course language, subtitle, and topic, as well as entering a rich text description
 * and specifying whether sign language is available. It also includes a nested Tags component.
 *
 * Integrates with Formik for form state and Redux for fetching selectable options.
 */
export const Content: React.FC = () => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const { values, setFieldValue, errors, isValid, submitCount, dirty } =
    useFormikContext<EventFormData | ELearningFormData>();

  /**
   * Determines if validation error for description should be displayed.
   */
  function checkValidation() {
    return errors.description && !isValid && submitCount > 0 && dirty;
  }

  /**
   * Fetch language, subtitle, and topic data when component mounts.
   */
  useEffect(() => {
    dispatch(getCourseLanguages());
    dispatch(getCourseSubtitles());
    dispatch(getCourseTopics());
  }, []);

  // Redux state selectors
  const courseLanguageOptions = useSelector(
    (state: AppState) => state.courseLanguages.courseLanguages
  );
  const courseSubtitleOptions = useSelector(
    (state: AppState) => state.courseSubtitles.courseSubtitles
  );
  const courseTopicOptions = useSelector(
    (state: AppState) => state.courseTopics.courseTopics
  );

  /**
   * Maps the course options to a format suitable for the select component.
   *
   * @param items - Array of items to map.
   * @returns Mapped options for the select component.
   */
  const mapOptions = (
    items: { id: string; name: string }[] | undefined
  ): { key: string; value: string; label: string }[] => {
    return [
      { key: "", value: "", label: "-" },
      ...(items?.map((item) => ({
        key: item.id,
        value: item.id,
        label: item.name,
      })) ?? []),
    ];
  };

  // Map options for select components
  const courseLanguageDropdownOptions = mapOptions(courseLanguageOptions);
  const courseSubtitleDropdownOptions = mapOptions(courseSubtitleOptions);
  const courseTopicsDropdownOptions = mapOptions(courseTopicOptions);

  // Handle select changes
  const createSelectHandler = useCallback(
    (fieldName: string, options: { id: string }[]) =>
      (event: React.ChangeEvent<{ value: unknown }>) => {
        const selectedId = event.target.value as string;

        if (!selectedId) {
          setFieldValue(fieldName, null);
          return;
        }

        const selectedObject = options.find((item) => item.id === selectedId);
        setFieldValue(fieldName, selectedObject ?? null);
      },
    [setFieldValue]
  );

  return (
    <>
      <Box style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <Grid container spacing={4}>
          <Grid item xs={12} sm={4}>
            <CustomSelect
              name="course_language"
              label="Kurssprache"
              id="course_language"
              labelId="course_language"
              value={values.course_language?.id ?? ""}
              onChange={createSelectHandler("course_language", courseLanguageOptions)}
              options={courseLanguageDropdownOptions}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <CustomSelect
              name="course_subtitle"
              label="Kursuntertitel"
              id="course_subtitle"
              labelId="course_subtitle"
              value={values.course_subtitle?.id ?? ""}
              onChange={createSelectHandler("course_subtitle", courseSubtitleOptions)}
              options={courseSubtitleDropdownOptions}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <CustomSelect
              name="topic"
              label="Thema"
              id="course_topic"
              labelId="course_topic"
              value={values.topic?.id ?? ""}
              onChange={createSelectHandler("topic", courseTopicOptions)}
              options={courseTopicsDropdownOptions}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box display="flex" alignItems="center" height="100%">
              <CustomCheckbox
                name="has_sign_language"
                label="Gebärdensprache"
                checked={values.has_sign_language}
                onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                  setFieldValue("has_sign_language", event.target.checked);
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Grid item xs={12}>
        <h3>Beschreibung</h3>

        <MUIRichTextEditor
          onChange={(changedVal) => {
            setFieldValue("description", changedVal.getCurrentContent());
          }}
          readOnly={values.status === EventStatus.Completed}
          defaultValue={JSON.stringify(convertToRaw(values.descriptionState))}
          label={`Beschreiben Sie Ihre Veranstaltung ${
            values.status === EventStatus.Published ? "*" : ""
          }`}
          classes={{
            root: checkValidation() ? classes.errorRoot : undefined,
            editor: checkValidation() ? classes.error : undefined,
          }}
          controls={[
            "title",
            "bold",
            "italic",
            "underline",
            "undo",
            "redo",
            "numberList",
            "bulletList",
            "indentLine",
            "link",
          ]}
          customControls={[
            {
              name: "indentLine",
              icon: <FormatIndentIncreaseIcon />,
              type: "inline",
              inlineStyle: {
                marginLeft: "1.25rem",
              },
            },
          ]}
          draftEditorProps={{
            spellCheck: true,
            stripPastedStyles: true,
          }}
        />
        {checkValidation() && (
          <FormHelperText variant="filled" error={checkValidation()}>
            {errors.description}
          </FormHelperText>
        )}
      </Grid>
      <Grid item xs={12} sm={8}>
        <Tags />
      </Grid>
    </>
  );
};
